import { Tooltip } from 'antd';
import {
  CSSProperties,
  memo,
  RefObject,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import AutoSizer from 'react-virtualized-auto-sizer';
import { areEqual, FixedSizeList as List } from 'react-window';
import styled, { css } from 'styled-components';

import { ArrowLeftSvg, PlusSvg } from '../../../assets/icons';
import { RecycleSvg } from '../../../assets/icons/RecycleSvg';
import theme from '../../../assets/theme';
import useConsumeGenerator from '../../../hooks/useConsumeGenerator';
import { GeneratorTabState } from '../../../hooks/useDesignerUIState';
import { useLongPress } from '../../../hooks/useLongPress';
import { useMetrics } from '../../../hooks/useMetrics';
import { LessonAttachment } from '../../../services/backendService/backendService';
import { RoundMedia } from '../../../services/backendService/types';
import { generatorFormStateToUserInputFormData } from '../../../utils/aiGeneratorUtils';
import { typeToActivityMap } from '../../../utils/creator';
import { zIndexes } from '../../../utils/zIndexes';
import { Button } from '../../_atoms/Button';
import { IconWrapper } from '../../_atoms/IconWrapper';
import { roundTypeToIcon } from '../../_molecules/AddNewActivity/AddNewActivity';
import ProgressBar from '../../_molecules/ProgressBar';
import { Slide } from '../../SlideComponents/slide.types';
import SlideWrapperViwer from '../../SlideComponents/SlideWrapperViewer';

type SlideToImport = Slide & {
  selected: boolean;
};

const Title = styled.h3<{ $fontSize: number }>`
  text-align: left;
  width: 100%;
  font-weight: bold;
  font-size: ${({ $fontSize }) => $fontSize}px;
  line-height: 140%;
  color: ${theme.colors.greyVeryDark};
  display: flex;
  align-items: center;
  margin-bottom: 0px;

  @media (max-width: 500px) {
    font-size: ${({ $fontSize }) => $fontSize - 5}px;
  }
`;

const BackButtonRowContainer = styled.div`
  display: flex;
  height: 30px;
  align-items: center;
  cursor: pointer;
  margin-bottom: 10px;
  padding: 0px 20px;
`;

const ImportDesignerSlideItemNumber = styled.div<{ $selected: boolean }>`
  position: absolute;
  left: 0px;
  bottom: 0px;
  font-weight: bold;
  color: ${({ $selected }) => ($selected ? theme.colors.white : theme.colors.black)};
  cursor: pointer;
  width: 40px;
  justify-content: center;
  display: flex;
  border-radius: 0 8px 0 8px;
  background-color: ${({ $selected }) =>
    $selected ? theme.colors.orange : theme.colors.grey};
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
`;

const ActivityOverlayItem = styled.div<{ $selected?: boolean }>`
  position: absolute;
  top: 0px;
  left: 0px;
  border-radius: 8px 0 8px 0;
  padding: 10px;
  z-index: ${zIndexes('ACTIVITY_OVERLAY_ITEM')};
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  ${({ $selected }) =>
    $selected
      ? css`
          background-color: ${theme.colors.orange};
        `
      : css`
          background-color: ${theme.colors.grey};
        `}
`;

const TabContainer = styled.div`
  height: 95%;
`;

const SlidesContainer = styled.div`
  height: calc(100% - 128px);
  overflow-y: auto;
`;

const DesignerSlideItem = ({
  slide,
  onClick,
  isDiscoverTemplate,
  isOwner,
  lessonAttachments,
}: {
  slide: Slide;
  onClick: () => void;
  isDiscoverTemplate: boolean;
  lessonAttachments?: LessonAttachment[];
  isOwner: boolean;
}) => {
  const [isHovering, setIsHovering] = useState(false);

  const longPressEvent = useLongPress({
    onClick: () => {
      onClick();
    },

    shouldPreventDefault: true,
    delay: 500,
  });
  return (
    <div
      style={{
        height: 175,
        width: '100%',
        marginBottom: 6,
        borderRadius: 10,
        overflow: 'hidden',
        position: 'relative',
        zIndex: zIndexes('CREATOR_TEMPLATE_SLIDE_OVERLAY'),
      }}
      onMouseOver={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div
        style={{
          height: '100%',
          width: '100%',
          position: 'absolute',
          left: 0,
          bottom: 0,
          borderRadius: 10,
          cursor: 'pointer',
        }}
      >
        <HoverableOverlay
          style={{
            height: '100%',
            width: '100%',
            position: 'relative',
            borderRadius: 10,
            zIndex: zIndexes('CREATOR_TEMPLATE_SLIDE_OVERLAY'),
          }}
        >
          {isHovering && (
            <div
              style={{
                borderRadius: 10,
                height: '100%',
                width: '100%',
                background: 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              {...longPressEvent}
            >
              <IconWrapper height="30px" width="30px" color={theme.colors.white}>
                <PlusSvg />
              </IconWrapper>
            </div>
          )}
        </HoverableOverlay>
      </div>
      <SlideWrapperViwer
        aspect={{ height: 1080, width: 1920 }}
        slide={slide}
        backgroundMedia={slide.backgroundMedia}
        resultView={false}
        showStaticPoll={true}
        hideLogo={false}
        isDiscoverTemplate={isDiscoverTemplate}
        lessonAttachments={lessonAttachments}
        isOwner={isOwner}
      ></SlideWrapperViwer>
    </div>
  );
};

const HoverableOverlay = styled.div`
  &:hover {
    background: rgba(0, 0, 0, 0.3);
    transition: ${theme.effects.defaultTransition};
  }
`;

const ColorStyles = [
  {
    backgroundColor: '#F9F3EB',
    shapeColor: '#F6E2C9',
  },
  { backgroundColor: '#FFCFA7', shapeColor: '#FFAD69' },
  { backgroundColor: '#61C2F8', shapeColor: '#39AEF0' },
  { backgroundColor: '#73CAB6', shapeColor: '#50B59E' },
  { backgroundColor: '#E1DAFF', shapeColor: '#C2AFF5' },
  { backgroundColor: '#fdbab3', shapeColor: '#fa8d81' },
];
const QuickAddSlidesExperience = ({
  onAddRounds,
  onGoBack,
  rounds,
  title,
  metricsContext,
  isDiscoverTemplate,
  quickAddListRef,
  generatorTabState,
  isGeneratorTab,
  setGeneratorTabState,
  style,
  isOwner,
}: {
  onGoBack: () => void;
  rounds: Slide[];
  title?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metricsContext: Record<string, any>;
  onAddRounds: (rounds: Slide[]) => void;
  isDiscoverTemplate: boolean;
  isOwner: boolean;
  quickAddListRef: RefObject<List>;
  generatorTabState?: GeneratorTabState;
  isGeneratorTab?: boolean;
  style?: CSSProperties;
  setGeneratorTabState?: (generatorTabState: GeneratorTabState) => void;
}) => {
  const { t } = useTranslation('Host');
  const metrics = useMetrics();
  const { consumeGenerator, isLoading: isRegenerating } = useConsumeGenerator();
  const [selectedColorStyle, setSelectedColorStyle] = useState<{
    backgroundColor: string;
    shapeColor: string;
  } | null>(null);

  const visibleSlides = useMemo(() => {
    const visibleRounds = rounds.filter(
      r => r.type !== 'voting' && r.type !== 'podium' && r.type !== 'responsegrid',
    );
    return visibleRounds;
  }, [rounds]);
  const isLoading = isRegenerating;

  const handleApply = () => {
    metrics.logEvent('AddSlidesExperience.QuickAdd.All', { ...metricsContext });
    onAddRounds(visibleSlides);
  };

  const handleColorStyleChanges = useCallback(
    ({
      backgroundColor,
      shapeColor,
    }: {
      backgroundColor: string;
      shapeColor: string;
      ignoreImageBackgrounds?: boolean;
    }) => {
      if (generatorTabState === undefined || setGeneratorTabState === undefined) return;
      metrics.logEvent('Generator.changeStyle', { backgroundColor, shapeColor });
      localStorage.setItem('generatorColorStyle', backgroundColor || '');
      setSelectedColorStyle(backgroundColor ? { backgroundColor, shapeColor } : null);
      if (backgroundColor) {
        const coloredSlides = changeColorsOnSlides({
          slides: generatorTabState.generatedSlides,
          backgroundColor,
          shapeColor,
          ignoreUnsplashImages: true,
        });

        setGeneratorTabState({
          ...generatorTabState,
          generatedSlides: coloredSlides,
        });
      }
    },
    [generatorTabState, setGeneratorTabState, metrics],
  );

  const handleRegenerate = () => {
    if (!generatorTabState) return;

    const generator = generatorTabState.selectedGeneratorNew;

    if (generator && generator.aiContent) {
      const formState = generatorTabState.generatorFormState;
      if (!generator) return;
      if (!formState) return;
      if (!setGeneratorTabState) return;
      const userInputFormData = generatorFormStateToUserInputFormData(
        formState,
        generator.aiContent.promptInputs,
      );

      consumeGenerator({
        generatorId: generator.id,
        userInputFormData,
        generatorName: generator.title || undefined,
        generatorPlan: generator?.aiContent?.generatorPlan,
        isRetry: true,
        onSuccessFunction: ({
          slides,
          attachments,
        }: {
          slides: Slide[];
          attachments?: LessonAttachment[];
        }) => {
          setGeneratorTabState({
            ...generatorTabState,
            generatedSlides: slides,
            generatedAttachments: attachments,
          });
        },
      });
      return;
    }
  };

  const itemData = useMemo(
    () => ({
      onGoBack,
      onAddRounds,
      title,
      isDiscoverTemplate,
      rounds: visibleSlides,
      lessonAttachments: generatorTabState?.generatedAttachments,
      quickAddListRef,
      metricsContext,
      isOwner,
    }),
    [
      onGoBack,
      onAddRounds,
      title,
      isDiscoverTemplate,
      visibleSlides,
      quickAddListRef,
      metricsContext,
      isOwner,
      generatorTabState?.generatedAttachments,
    ],
  );

  return (
    <TabContainer style={{ ...style, paddingTop: 20 }}>
      <BackButtonRowContainer
        onClick={() => {
          metrics.logEvent('AddSlidesExperience.GoBack', {
            ...metricsContext,
          });
          onGoBack();
        }}
      >
        <IconWrapper height="30px" width="30px" color={theme.colors.black}>
          <ArrowLeftSvg />
        </IconWrapper>
        {title && (
          <Title $fontSize={18}>
            {isGeneratorTab ? title : title.substring(0, 26)}{' '}
            {!isGeneratorTab && title.length > 26 ? '...' : ''}
          </Title>
        )}
      </BackButtonRowContainer>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          gap: '5px',
          padding: '0px 20px',
          marginBottom: 6,
        }}
      >
        {isGeneratorTab && !isLoading && (
          <ColorSelectorContainer>
            {ColorStyles.map((colorStyle, index) => (
              <Tooltip
                title={'Change the colors of the generated slides'}
                mouseEnterDelay={1}
                key={index}
              >
                <ColorButton
                  style={{
                    backgroundColor: colorStyle.backgroundColor,
                  }}
                  $isSelected={
                    selectedColorStyle?.backgroundColor === colorStyle.backgroundColor
                  }
                  onClick={() =>
                    handleColorStyleChanges({
                      backgroundColor:
                        selectedColorStyle?.backgroundColor === colorStyle.backgroundColor
                          ? ''
                          : colorStyle.backgroundColor,
                      shapeColor: colorStyle.shapeColor,
                      ignoreImageBackgrounds: false,
                    })
                  }
                >
                  <ColorClipPath style={{ backgroundColor: colorStyle.shapeColor }} />
                </ColorButton>
              </Tooltip>
            ))}
          </ColorSelectorContainer>
        )}
        {(isLoading || generatorTabState?.isLoadingGeneratedRounds) && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              justifyItems: 'center',
              padding: 20,
            }}
          >
            <ProgressBar title={t('Generating')} isSmallTemplates={true} />
          </div>
        )}

        {!isLoading && !generatorTabState?.isLoadingGeneratedRounds && (
          <AddAndGenerateContainer>
            {isGeneratorTab && (
              <Button
                type="ghost"
                onClick={handleRegenerate}
                icon={<RecycleSvg />}
                styles={{ padding: '10px', fontSize: 16 }}
              >
                {t('Re-generate')}
              </Button>
            )}

            <Button
              type={isDiscoverTemplate ? 'ghost' : 'primary'}
              styles={{
                background: 'transparent',
                color: theme.colors.black,
                width: isDiscoverTemplate ? '100%' : 'auto',
                textDecoration: 'underline',
                margin: isDiscoverTemplate ? 'auto' : '0px',
                boxShadow: 'none',
              }}
              onClick={handleApply}
            >
              {visibleSlides.length > 1 ? (
                <>
                  {t('addAddSlides', {
                    defaultValue: 'Add all {{count}} slides',
                    count: visibleSlides.length,
                  })}
                </>
              ) : (
                <>{t('Add slide')}</>
              )}
            </Button>
          </AddAndGenerateContainer>
        )}
      </div>
      <SlidesContainer
        id="scrollable_1"
        style={{ height: isGeneratorTab ? 'calc(100% - 141px)' : 'calc(100% - 90px)' }}
      >
        {!isLoading &&
          !generatorTabState?.isLoadingGeneratedRounds &&
          visibleSlides.length > 0 && (
            <AutoSizer style={{ width: '100%', margin: '0 auto' }}>
              {({ height, width }: { height: number; width: number }) => {
                return (
                  <List
                    style={{
                      borderRadius: '10px 10px 0px 0px',
                      width: '100%',
                    }}
                    ref={quickAddListRef}
                    height={height}
                    itemCount={visibleSlides.length}
                    itemData={itemData}
                    itemSize={215}
                    layout={'vertical'}
                    width={width}
                  >
                    {QuickAddRow}
                  </List>
                );
              }}
            </AutoSizer>
          )}
      </SlidesContainer>
    </TabContainer>
  );
};

//TODO: rename rounds variables to slides
const CoreAddSlidesExperience = ({
  onAddRounds,
  onGoBack,
  rounds,
  title,
  isDiscoverTemplate,
  metricsContext,
  generatorTabState,
  isGeneratorTab,
  setGeneratorTabState,
  style,
  isOwner,
}: {
  onGoBack: () => void;
  title?: string;
  rounds: Slide[];
  onAddRounds: (rounds: Slide[]) => void;
  isDiscoverTemplate: boolean;
  isOwner: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metricsContext: Record<string, any>;
  generatorTabState?: GeneratorTabState;
  isGeneratorTab?: boolean;
  setGeneratorTabState?: (generatorTabState: GeneratorTabState) => void;
  style?: CSSProperties;
}) => {
  const quickAddListRef = useRef<List>(null);

  return (
    <QuickAddSlidesExperience
      onAddRounds={onAddRounds}
      onGoBack={onGoBack}
      rounds={rounds}
      metricsContext={metricsContext}
      title={title}
      isDiscoverTemplate={isDiscoverTemplate}
      isOwner={isOwner}
      quickAddListRef={quickAddListRef}
      generatorTabState={generatorTabState}
      isGeneratorTab={isGeneratorTab}
      setGeneratorTabState={setGeneratorTabState}
      style={style}
    />
  );
};

export default CoreAddSlidesExperience;

const QuickAddRow = memo(
  ({
    data,
    index,
    style,
  }: {
    data: {
      onAddRounds: (rounds: Slide[]) => void;
      isDiscoverTemplate: boolean;
      rounds: Slide[];
      lessonAttachments?: LessonAttachment[];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      metricsContext: Record<string, any>;
      isOwner: boolean;
    };
    index: number;
    style: CSSProperties;
  }) => {
    const metrics = useMetrics();
    const { onAddRounds, isDiscoverTemplate, rounds, metricsContext, isOwner } = data;
    const slide = rounds[index];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const preventKeyboardScroll = (e: any) => {
      if (
        e.key === 'ArrowUp' ||
        e.key === 'ArrowDown' ||
        e.key === 'ArrowLeft' ||
        e.key === 'ArrowRight'
      ) {
        // Make sure we sill propagate event bubbling to let the Creator notice keyboard presses
        e.preventDefault();
      }
    };

    const activity = typeToActivityMap(slide.type);

    return (
      <div style={style} key={index} onKeyDown={preventKeyboardScroll}>
        <div key={slide.id} style={{ width: 310, margin: '0 auto', fontSize: 15 }}>
          <span
            style={{
              marginBottom: 0,
              textAlign: 'left',
              display: 'flex',
              gap: '5px',
              alignItems: 'center',
            }}
          >
            <IconWrapper
              style={{ opacity: 1 }}
              color="black"
              height={'15px'}
              width={'15px'}
            >
              {roundTypeToIcon(slide.type)}
            </IconWrapper>
            {index + 1}. {activity && activity.title}
          </span>
          <DesignerSlideItem
            onClick={() => {
              metrics.logEvent('AddSlidesExperience.QuickAdd.Single', {
                ...metricsContext,
                ...slide,
              });
              onAddRounds([slide]);
            }}
            slide={slide}
            isDiscoverTemplate={isDiscoverTemplate}
            lessonAttachments={data.lessonAttachments}
            isOwner={isOwner}
          />
        </div>
      </div>
    );
  },
  areEqual,
);
QuickAddRow.displayName = 'QuickAddRow';

const MultiSelectRow = memo(
  ({
    data,
    index,
    style,
  }: {
    data: {
      title?: string;
      selectedRounds: SlideToImport[];
      setSelectedRounds: (round: SlideToImport[]) => void;
      isDiscoverTemplate: boolean;
    };
    index: number;
    style: CSSProperties;
  }) => {
    const { selectedRounds, setSelectedRounds, isDiscoverTemplate } = data;

    const selectedRound = selectedRounds[index];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const preventKeyboardScroll = (e: any) => {
      if (
        e.key === 'ArrowUp' ||
        e.key === 'ArrowDown' ||
        e.key === 'ArrowLeft' ||
        e.key === 'ArrowRight'
      ) {
        // Make sure we sill propagate event bubbling to let the Creator notice keyboard presses
        e.preventDefault();
      }
    };

    const toggleSelected = (round: SlideToImport) => {
      setSelectedRounds(
        selectedRounds.map(r =>
          r.id === round.id ? { ...round, selected: !round.selected } : r,
        ),
      );
    };

    const activity = typeToActivityMap(selectedRound.type);

    return (
      <div style={style} key={index} onKeyDown={preventKeyboardScroll}>
        <div key={selectedRound.id} style={{ width: 310, margin: '0 auto' }}>
          <p style={{ marginBottom: 0 }}>
            {index + 1} {activity && activity.title}
          </p>
          <div
            style={{
              height: 175,
              width: '100%',
              marginBottom: 6,
              borderRadius: 10,
              position: 'relative',
              zIndex: zIndexes('CREATOR_TEMPLATE_SLIDE_OVERLAY'),
            }}
          >
            <div
              style={{
                height: '100%',
                width: '100%',
                position: 'absolute',
                left: 0,
                bottom: 0,
                borderRadius: 10,
                cursor: 'pointer',
              }}
              onClick={() => toggleSelected(selectedRound)}
            >
              <div
                style={{
                  height: '100%',
                  width: '100%',
                  position: 'relative',
                  borderRadius: 10,
                  zIndex: zIndexes('CREATOR_TEMPLATE_SLIDE_OVERLAY'),
                }}
              >
                {selectedRound.type !== 'slide' && (
                  <ActivityOverlayItem $selected={selectedRound.selected}>
                    <IconWrapper
                      color={
                        selectedRound.selected ? theme.colors.white : theme.colors.black
                      }
                      height={'20px'}
                      width={'20px'}
                    >
                      {roundTypeToIcon(selectedRound.type)}
                    </IconWrapper>
                  </ActivityOverlayItem>
                )}
                <ImportDesignerSlideItemNumber $selected={selectedRound.selected}>
                  {index + 1}
                </ImportDesignerSlideItemNumber>
              </div>
            </div>
            <SlideWrapperViwer
              aspect={{ height: 1080, width: 1920 }}
              slide={selectedRound}
              backgroundMedia={selectedRound.backgroundMedia}
              resultView={false}
              showStaticPoll={true}
              hideLogo={false}
              isDiscoverTemplate={isDiscoverTemplate}
            />
          </div>
        </div>
      </div>
    );
  },
  areEqual,
);
MultiSelectRow.displayName = 'MultiSelectRow';

const changeColorsOnSlides = ({
  slides,
  backgroundColor,
  shapeColor,
  ignoreUnsplashImages,
}: {
  slides: Slide[];
  backgroundColor: string;
  shapeColor: string;
  ignoreUnsplashImages?: boolean;
}) => {
  return slides.map(r => {
    if (
      !!r.backgroundMedia?.fileMimeType &&
      r.backgroundMedia?.fileMimeType !== 'image/color'
    )
      return r;
    if (ignoreUnsplashImages && r.backgroundMedia?.fileMimeType === 'image/unsplash')
      return r;
    const newBackgroundMedia: RoundMedia = {
      ...r.backgroundMedia,
      fileId: backgroundColor,
      fileMimeType: 'image/color',
    };
    return {
      ...r,
      backgroundMedia: newBackgroundMedia,
      elements: r.elements.map(e => {
        if (e?.colors && e.colors.length > 1) return e;
        if (e.type === 'shape' || e.type === 'svg') {
          return {
            ...e,
            colors: [shapeColor],
          };
        }
        return e;
      }),
    };
  });
};

const ColorSelectorContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  overflow-x: auto;
  gap: 10px;
  align-items: center;
  max-width: 500px;
  margin: 10px auto;
`;

const ColorButton = styled.div<{ $isSelected: boolean }>`
  cursor: pointer;
  width: 40px;
  height: 40px;
  border-radius: 5px;
  border: ${({ $isSelected }) =>
    $isSelected ? `1px solid ${theme.colors.black}` : `1px solid ${theme.colors.grey}`};
`;
const ColorClipPath = styled.div`
  clip-path: ellipse(49% 49% at 57% 3%);
  width: 100%;
  border-radius: 5px;
  height: 100%;
`;

const AddAndGenerateContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 10px;
  max-width: 500px;
  margin: 10px auto;
`;
